import React, { useEffect } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    SimpleGrid,
    Icon,
    Card,
    CardBody,
    CardHeader,
    Button,
    List,
    ListItem,
    ListIcon,
    HStack,
} from "@chakra-ui/react";
import { FaCheck, FaUsers, FaCrown } from "react-icons/fa";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import PublicForm from "./leadflow/PublicForm";

const Pricing = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Sales switch (2026-10-24, Tom): "Coming Soon" stays on until REACT_APP_SALES_OPEN=true is set for
    // the build. Only Getting Started is sold (product getting-started in the bills module).
    const salesOpen = process.env.REACT_APP_SALES_OPEN === "true";

    // Sam Manger, website feedback 27 Jul 2026: one plan only (no "choose your plan", no three plans),
    // a single purchase (no "cancel anytime"), "evidence-informed", no outcome promises.
    const pricingPlans = [
        {
            name: "The SurgReady program",
            price: "$47",
            period: "one-off",
            description: "The complete surgical preparation program: 12 evidence-informed modules, yours to keep working through.",
            features: [
                "All 12 surgical preparation modules",
                "Nutrition and supplements",
                "Physical activity and sleep",
                "Mental wellbeing and managing stress",
                "Medication and chronic disease management",
                "Health assessment and a health record to share with your healthcare team",
                "Progress tracking and completion certificates",
            ],
            buttonText: salesOpen ? "Get started" : "Coming Soon",
            icon: FaCrown,
            active: salesOpen,
            slug: "getting-started",
        },
    ];

    return (
        <Box bg="#000000" minHeight="100vh" color="white">
            <NavbarWithCallToAction />


            <Container maxW="1200px" py={12}>
                <VStack spacing={12} align="stretch">
                    {/* Hero Section */}
                    <Box textAlign="center" py={10}>
                        <Heading
                            as="h1"
                            fontSize={{ base: "3xl", md: "5xl" }}
                            fontWeight="500"
                            letterSpacing="-0.02em"
                            mb={6}
                        >
                            Get SurgReady
                        </Heading>
                        <Text fontSize="xl" color="whiteAlpha.700" maxW="800px" mx="auto" mb={6}>
                            An evidence-informed online program to help you prepare for surgery and feel more confident going in.
                        </Text>
                    </Box>

                    {/* Pricing Cards */}
                    <SimpleGrid columns={1} spacing={8} alignItems="stretch" maxW="520px" mx="auto" w="full">
                        {pricingPlans.map((plan, index) => (
                            <Card
                                key={index}
                                bg={plan.active ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.04)"}
                                boxShadow={plan.active ? "0 8px 40px rgba(74, 144, 226, 0.2)" : "none"}
                                borderRadius="2xl"
                                borderWidth={plan.active ? "2px" : "1px"}
                                borderColor={plan.active ? "#4A90E2" : "whiteAlpha.200"}
                                position="relative"
                                overflow="hidden"
                                opacity={plan.active ? 1 : 0.75}
                                transition="all 0.3s ease"
                                _hover={plan.active ? {
                                    transform: "translateY(-5px)",
                                    boxShadow: "0 12px 50px rgba(74, 144, 226, 0.3)"
                                } : undefined}
                            >
                                {plan.active && (
                                    <Box
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        right={0}
                                        bg="#4A90E2"
                                        color="white"
                                        py={2}
                                        textAlign="center"
                                        fontSize="sm"
                                        fontWeight="bold"
                                        letterSpacing="0.05em"
                                    >
                                        Get Prepared
                                    </Box>
                                )}

                                {!plan.active && (
                                    <Box
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        right={0}
                                        bg="whiteAlpha.200"
                                        color="whiteAlpha.600"
                                        py={2}
                                        textAlign="center"
                                        fontSize="sm"
                                        fontWeight="bold"
                                        letterSpacing="0.05em"
                                    >
                                        Coming Soon
                                    </Box>
                                )}

                                <CardHeader
                                    bg={plan.active ? "rgba(74, 144, 226, 0.08)" : "rgba(255,255,255,0.02)"}
                                    py={6}
                                    pt={10}
                                >
                                    <VStack spacing={2}>
                                        <Icon
                                            as={plan.icon}
                                            w={10}
                                            h={10}
                                            color={plan.active ? "#4A90E2" : "whiteAlpha.500"}
                                        />
                                        <Heading
                                            size="lg"
                                            color={plan.active ? "white" : "whiteAlpha.600"}
                                        >
                                            {plan.name}
                                        </Heading>
                                        <Text
                                            fontSize="sm"
                                            color={plan.active ? "whiteAlpha.700" : "whiteAlpha.500"}
                                            textAlign="center"
                                        >
                                            {plan.description}
                                        </Text>
                                    </VStack>
                                </CardHeader>

                                <CardBody p={6}>
                                    <VStack spacing={6} align="stretch">
                                        <Box textAlign="center">
                                            <HStack justify="center" align="baseline">
                                                <Text
                                                    fontSize="4xl"
                                                    fontWeight="bold"
                                                    color={plan.active ? "white" : "whiteAlpha.300"}
                                                >
                                                    {plan.price}
                                                </Text>
                                                <Text
                                                    color={plan.active ? "whiteAlpha.600" : "whiteAlpha.300"}
                                                    fontSize="md"
                                                >
                                                    {plan.period}
                                                </Text>
                                            </HStack>
                                            <Text fontSize="sm" color="whiteAlpha.600">AUD</Text>
                                        </Box>

                                        <List spacing={3}>
                                            {plan.features.map((feature, featureIndex) => (
                                                <ListItem
                                                    key={featureIndex}
                                                    fontSize="sm"
                                                    color={plan.active ? "whiteAlpha.800" : "whiteAlpha.500"}
                                                >
                                                    <ListIcon
                                                        as={FaCheck}
                                                        color={plan.active ? "#4A90E2" : "whiteAlpha.400"}
                                                    />
                                                    {feature}
                                                </ListItem>
                                            ))}
                                        </List>

                                        {plan.active ? (
                                            <Button
                                                size="lg"
                                                rightIcon={<ChevronRightIcon />}
                                                as={RouterLink}
                                                to={`/subscribe/${plan.slug}`}
                                                width="100%"
                                                py={6}
                                                bg="#4A90E2"
                                                color="white"
                                                borderRadius="full"
                                                fontWeight="600"
                                                textTransform="uppercase"
                                                letterSpacing="0.1em"
                                                boxShadow="0 4px 20px rgba(74, 144, 226, 0.4)"
                                                _hover={{
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 8px 30px rgba(74, 144, 226, 0.5)",
                                                    bg: "#3DA2DD",
                                                }}
                                                transition="all 0.3s ease"
                                            >
                                                {plan.buttonText}
                                            </Button>
                                        ) : (
                                            <Button
                                                size="lg"
                                                width="100%"
                                                py={6}
                                                isDisabled
                                                variant="outline"
                                                borderColor="whiteAlpha.200"
                                                color="whiteAlpha.400"
                                                borderRadius="full"
                                                _disabled={{
                                                    opacity: 0.5,
                                                    cursor: "not-allowed",
                                                }}
                                            >
                                                {plan.buttonText}
                                            </Button>
                                        )}
                                    </VStack>
                                </CardBody>
                            </Card>
                        ))}
                    </SimpleGrid>

                    {/* Waitlist — the paid plans aren't live yet, so capture interest here (feeds LeadFlow).
                        Hidden once sales are open (same switch as the plan cards). */}
                    {!salesOpen && <Box
                        p={{ base: 6, md: 8 }}
                        bg="rgba(74, 144, 226, 0.08)"
                        border="1px solid"
                        borderColor="rgba(74, 144, 226, 0.2)"
                        borderRadius="2xl"
                        textAlign="center"
                    >
                        <Heading as="h2" size="lg" mb={2}>
                            Not available yet — join the waitlist
                        </Heading>
                        <Text fontSize="md" color="whiteAlpha.700" mb={6} maxW="600px" mx="auto">
                            SurgReady is in final preparation. Leave your details and we'll email you
                            the moment the program opens.
                        </Text>
                        <Box maxW="md" mx="auto">
                            <PublicForm slug="waitlist" embed redirectTo={null} />
                        </Box>
                    </Box>}

                    {/* Healthcare Provider Section */}
                    <Box
                        p={8}
                        bg="rgba(74, 144, 226, 0.08)"
                        border="1px solid"
                        borderColor="rgba(74, 144, 226, 0.2)"
                        borderRadius="2xl"
                        textAlign="center"
                    >
                        <Icon as={FaUsers} w={12} h={12} color="#4A90E2" mb={4} />
                        <Heading as="h2" size="lg" mb={4}>
                            Are you a healthcare provider?
                        </Heading>
                        <Text fontSize="lg" color="whiteAlpha.700" mb={6} maxW="600px" mx="auto">
                            Healthcare providers and insurers can purchase SurgReady in bulk packages, with
                            special pricing, to provide to their patients.
                        </Text>
                        <Button
                            as={RouterLink}
                            to="/contact"
                            size="lg"
                            px={8}
                            py={6}
                            bg="#4A90E2"
                            color="white"
                            borderRadius="full"
                            fontWeight="600"
                            _hover={{
                                bg: "#3DA2DD",
                                transform: "translateY(-2px)",
                            }}
                            transition="all 0.3s ease"
                        >
                            Contact us
                        </Button>
                    </Box>

                </VStack>
            </Container>

            <FooterWithFourColumns />
        </Box>
    );
};

export default Pricing;
